import React from 'react';
import { Link } from 'react-router-dom';

const sampleCsvHref = `${import.meta.env.BASE_URL}templates/quiz-google-sheet-sample.csv`;

export default function QuizGoogleSheetGuidePage() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-cyan-50/80 via-white to-emerald-50/40 text-slate-900">
      <header className="border-b border-cyan-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3 md:px-6">
          <Link to="/" className="font-headline text-lg font-extrabold text-cyan-600 hover:text-cyan-700">
            ← QuizAtelier
          </Link>
          <Link
            to="/login"
            className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Đăng nhập
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-3xl space-y-8 px-4 py-10 md:px-6 md:py-14">
        <header className="space-y-3">
          <p className="text-sm font-bold uppercase tracking-wide text-cyan-700">Hướng dẫn · Google Sheet</p>
          <h1 className="font-headline text-3xl font-extrabold text-slate-900 md:text-4xl">
            Tạo Google Sheet để import bài test
          </h1>
          <p className="text-base leading-relaxed text-slate-600">
            Bạn có thể soạn câu hỏi trong Google Sheet, chia sẻ quyền xem, rồi dán link vào mục import trên trang{' '}
            <Link to="/quizzes/create" className="font-semibold text-cyan-700 underline decoration-cyan-200 underline-offset-2">
              Tạo quiz
            </Link>{' '}
            (cần đăng nhập). Hệ thống đọc bảng tính dưới dạng CSV — dòng đầu là tên cột, mỗi dòng sau là một câu hỏi.
          </p>
          <p className="text-sm leading-relaxed text-slate-500">
            <span className="font-semibold text-slate-600">English:</span> Create a Google Sheet, share it so anyone with
            the link can view, then paste the sheet URL into the import box on the quiz create page. The first row must
            be headers; each following row is one question. Required columns: <code className="rounded bg-slate-100 px-1">question</code>,{' '}
            <code className="rounded bg-slate-100 px-1">answer</code>.
          </p>
        </header>

        <section className="rounded-2xl border border-cyan-100 bg-white p-6 shadow-sm md:p-8">
          <h2 className="font-headline text-xl font-bold text-slate-900">1. Tạo và đặt tên sheet</h2>
          <ol className="mt-4 list-decimal space-y-2 pl-5 text-slate-700">
            <li>Mở Google Drive → Tạo → Google Trang tính.</li>
            <li>Đặt tên file (ví dụ: &quot;Kiểm tra Toán lớp 3&quot;).</li>
            <li>Dòng 1 gõ đúng tên cột (xem bảng bên dưới). Không gộp ô, không để hàng trống phía trên tiêu đề.</li>
          </ol>
        </section>

        <section className="rounded-2xl border border-cyan-100 bg-white p-6 shadow-sm md:p-8">
          <h2 className="font-headline text-xl font-bold text-slate-900">2. Chia sẻ để máy chủ đọc được</h2>
          <ol className="mt-4 list-decimal space-y-2 pl-5 text-slate-700">
            <li>
              Bấm <strong>Chia sẻ</strong> → đặt quyền <strong>Bất kỳ ai có liên kết</strong> là <strong>Người xem</strong>{' '}
              (hoặc tương đương “Anyone with the link — Viewer”).
            </li>
            <li>Sao chép URL dạng <code className="rounded bg-slate-100 px-1 text-sm">.../spreadsheets/d/.../edit...</code>.</li>
            <li>Dán URL đó vào ô import Google Sheet trên QuizAtelier — không cần tự xuất CSV.</li>
          </ol>
          <p className="mt-4 text-sm text-amber-800">
            Nếu sheet kín, import sẽ lỗi &quot;Không tải được dữ liệu&quot;. Tab đang dùng phải là tab đầu tiên hoặc tab mặc định
            khi mở link (Google xuất CSV theo thứ tự tab).
          </p>
        </section>

        <section className="rounded-2xl border border-cyan-100 bg-white p-6 shadow-sm md:p-8">
          <h2 className="font-headline text-xl font-bold text-slate-900">3. Cột (tiêu đề dòng 1)</h2>
          <p className="mt-2 text-sm text-slate-600">
            Tên cột <strong>không phân biệt hoa thường</strong>. Hai cột bắt buộc: <code className="rounded bg-slate-100 px-1">question</code>,{' '}
            <code className="rounded bg-slate-100 px-1">answer</code>.
          </p>
          <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[28rem] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="p-3 font-bold text-slate-800">Cột</th>
                  <th className="p-3 font-bold text-slate-800">Ý nghĩa</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {[
                  ['question', 'Nội dung câu hỏi (bắt buộc).'],
                  ['answer', 'Đáp án đúng (bắt buộc). Với trắc nghiệm: ghi đúng nội dung lựa chọn đúng, hoặc một chữ A/B/C/D nếu khớp thứ tự choice_1…4.'],
                  ['type', 'choice (trắc nghiệm) hoặc input (tự điền). Để trống: ≥2 lựa chọn → choice, ngược lại → input.'],
                  ['choice_1 … choice_4', 'Các đáp án trắc nghiệm (hoặc dùng option_1…, option_a…d). Cần ít nhất 2 lựa chọn có nội dung.'],
                  ['choices', 'Tuỳ chọn: JSON mảng hoặc chuỗi phân tách bằng | (thay cho cột choice_*).'],
                  ['hint', 'Gợi ý hiển thị khi làm bài.'],
                  ['explanation', 'Giải thích sau khi nộp bài.'],
                  ['points', 'Điểm câu (số). Mặc định 10.'],
                  ['id', 'Mã tham chiếu trong sheet (hệ thống vẫn tạo id riêng khi lưu).'],
                  ['image_url', 'Link ảnh minh họa (https…). Có thể dùng thêm image, url tùy bối cảnh.'],
                ].map(([col, desc]) => (
                  <tr key={col} className="border-b border-slate-100">
                    <td className="whitespace-nowrap p-3 font-mono text-xs text-cyan-800 md:text-sm">{col}</td>
                    <td className="p-3">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-2xl border border-cyan-100 bg-white p-6 shadow-sm md:p-8">
          <h2 className="font-headline text-xl font-bold text-slate-900">4. Mẹo định dạng CSV</h2>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-700">
            <li>
              Nếu câu hỏi hoặc đáp án có dấu phẩy, bọc cả ô bằng dấu ngoặc kép CSV:{' '}
              <code className="rounded bg-slate-100 px-1 text-xs">&quot;Chọn 2, 3 hoặc 5&quot;</code>.
            </li>
            <li>Trong Google Sheet thường tự xử lý khi bạn xuất — chỉ cần gõ nội dung bình thường trong ô.</li>
            <li>Có thể dùng dấu phẩy, chấm phẩy hoặc tab làm phân cách cột; hệ thống tự nhận diện theo dòng tiêu đề.</li>
          </ul>
        </section>

        <section className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 shadow-sm md:p-8">
          <h2 className="font-headline text-xl font-bold text-emerald-900">5. Tải file mẫu</h2>
          <p className="mt-2 text-slate-700">
            File CSV mẫu có sẵn vài câu (trắc nghiệm + tự điền). Bạn có thể mở bằng Excel hoặc tải lên Google Drive → Mở bằng
            Trang tính → chỉnh sửa theo nhu cầu.
          </p>
          <a
            href={sampleCsvHref}
            download="quiz-google-sheet-sample.csv"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-5 py-3 text-sm font-bold text-white shadow-md shadow-emerald-200 transition hover:bg-emerald-800"
          >
            <span className="material-symbols-outlined !text-xl">download</span>
            Tải quiz-google-sheet-sample.csv
          </a>
          <p className="mt-3 text-xs text-slate-600">
            English: Download the sample CSV, open it in Google Sheets, then share and import using your sheet URL.
          </p>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-slate-50/80 p-6 md:p-8">
          <h2 className="font-headline text-lg font-bold text-slate-900">Import trên ứng dụng</h2>
          <p className="mt-2 text-sm text-slate-600">
            Đăng nhập → <Link className="font-semibold text-cyan-700 underline" to="/quizzes/create">Tạo quiz</Link> → phần
            &quot;Import từ Google Sheet&quot; → dán link → bấm import. Bạn có thể chỉnh thêm tiêu đề, điểm đạt và câu hỏi sau khi
            import.
          </p>
        </section>
      </article>
    </main>
  );
}
